<script setup lang="ts">
import MiiaFwAuthModal from './AuthModal.vue'
import MiiaFwAlertMessageLight from './AlertMessageLight.vue'
import MiiaFwActivate from './AuthActivate.vue'
import { signup } from '../utils/miiaFwCognito'
import MiiaFwConfig from '@/configs/config'
import { reactive, ref, useTemplateRef } from 'vue'
import { useLoadingView } from '../composables/useLoadingView'

/**
 * @module components/miiafw/auth/MiiaFwSignup
 * @description 「サインアップ」画面の機能
 * @vue-data {Boolean} [modalTop = false] 最初に開くモ―ダルの制御。trueのときモーダルを開く
 * @vue-data {Boolean} [myModal = false] モーダル制御。trueのときモーダルを開く
 * @vue-data {String} [userName = 空白] ユーザId
 * @vue-data {String} [email = 空白] メールアドレス
 * @vue-data {String} [password = 空白] パスワード
 * @vue-data {String} [password2 = 空白] パスワード（再確認）
 */
const userNameInput = useTemplateRef<HTMLInputElement>('userNameInput')
const alertMessageLight =
  useTemplateRef<InstanceType<typeof MiiaFwAlertMessageLight>>('alertMessageLight')
const close = useTemplateRef<HTMLButtonElement>('close')
const activate = useTemplateRef<InstanceType<typeof MiiaFwActivate>>('activate')

const { setLoadingView } = useLoadingView()

const modalTop = ref(false)
const myModal = ref(false)
const userName = ref('')
const email = ref('')
const phoneNumber = ref('')
const password = ref('')
const password2 = ref('')
/**
 * フォームの表示制御のためのオブジェクト。
 * hasErrorはフォームの見栄えをAlertにし、
 * alertMessageはフォームの横にアラートメッセージを表示する
 */
const alertView = reactive<{ [key: string]: { hasError: boolean; alertMessage: string } }>({
  /** ユーザー名 */
  un: { hasError: false, alertMessage: '' },
  /** メールアドレス */
  em: { hasError: false, alertMessage: '' },
  /** 電話番号 */
  pn: { hasError: false, alertMessage: '' },
  /** パスワード */
  pw: { hasError: false, alertMessage: '' },
  /** 再入力されたパスワード */
  pw2: { hasError: false, alertMessage: '' }
})
const mfaEnable = ref(
  (Object.prototype.hasOwnProperty.call(MiiaFwConfig.config.authorization, 'authSubDir') &&
  typeof MiiaFwConfig.config.authorization.authSubDir === 'string'
    ? MiiaFwConfig.config.authorization.authSubDir
    : 'miiafw-auth') === 'miiafw-auth-mfa'
)

/** エラーメッセージを初期化 */
const resetAlertView = () => {
  Object.keys(alertView).forEach(key => {
    alertView[key].hasError = false
    alertView[key].alertMessage = ''
  })
}

/**
 * モーダルを開く
 * @param argUserId モーダルを開いたときにあらかじめ入力しておくID
 * @param argEmail モーダルを開いたときにあらかじめ入力しておくパスワード
 * @param argPhoneNumber モーダルを開いたときにあらかじめ入力しておく電話番号
 */
const openModal = (argUserId: string = '', argEmail: string = '', argPhoneNumber: string = '') => {
  if (argUserId.length > 0) {
    userName.value = argUserId
  }
  if (argEmail.length > 0) {
    email.value = argEmail
  }
  if (argPhoneNumber.length > 0) {
    phoneNumber.value = argPhoneNumber
  }
  modalTop.value = true
  myModal.value = true
}

/**
 * モーダル内のユーザID、メールアドレス、パスワード、パスワード（再確認用）、エラー表示を消去した後、モーダルを閉じる
 */
const closeMyModal = (): void => {
  userName.value = ''
  email.value = ''
  phoneNumber.value = ''
  password.value = ''
  password2.value = ''
  resetAlertView()
  myModal.value = false
}
/**
 * 特定条件でモーダルを閉じる。＜template＞で指定。
 */
const closeModal = (): void => {
  closeMyModal()
  modalTop.value = false
}

/**
 * モーダル上でTab操作をした際、モーダル上のオブジェクトを循環できるようにする
 * @param e キーボードイベント
 */
const firstTab = (e: KeyboardEvent): void => {
  e.preventDefault()
  e.stopPropagation()
  close.value?.focus()
}

/**
 * モーダル上でShift+Tab操作をした際、モーダル上のオブジェクトを循環できるようにする
 * @param e キーボードイベント
 */
const lastTab = (e: KeyboardEvent): void => {
  // Shift + Tabは、Tabの遡りなのでデフォルトイベントのまま
  if (!e.shiftKey) {
    e.preventDefault()
    e.stopPropagation()
    userNameInput.value?.focus()
  }
}

/**
 * モーダル内のユーザID、メールアドレス、パスワード、パスワード（再確認）、エラー表示を消去後、ユーザIDにフォーカス
 */
const doReset = (): void => {
  alertMessageLight.value?.deleteAlertMessage()
  userName.value = ''
  email.value = ''
  phoneNumber.value = ''
  password.value = ''
  password2.value = ''
  resetAlertView()
  userNameInput.value?.focus()
}

/**
 * ID自動発行およびサインアップAPI呼び出し
 */
const signUpApiCall = (userName: string, email: string, phoneNumber: string, password: string) => {
  // 処理中表示
  setLoadingView(true)
  // サインアップ処理
  const argPn = phoneNumber.length > 0 ? '+81' + phoneNumber.slice(1) : ''
  signup(userName, email, argPn, password)
    .then(() => {
      setLoadingView(false) // 必ずコンポーネント消滅前に実行
      closeMyModal()
      activate.value?.openModal(userName)
    })
    .catch(error => {
      if (error.message.match(/email/)) {
        alertView.em.hasError = true
      }
      if (error.message.match(/Password/) || error.message.match(/password/)) {
        alertView.pw.hasError = true
        alertView.pw2.hasError = true
      }
      if (
        error.message.match(/username/) ||
        error.message.match(/User/) ||
        error.message.match(/USERNAME/) ||
        error.message.match(/Username/) ||
        error.name === 'UsernameExistsException'
      ) {
        alertView.un.hasError = true
      }
      alertMessageLight.value?.addAlertMessage('・' + error.message)
      setLoadingView(false) // 必ずコンポーネント消滅前に実行
    })
}

/**
 * ユーザID、メールアドレス、パスワード、パスワード（再確認用）のエラー表示機能。表示されているエラーを初期化し、各項目に対してバリデーションチェックを行う。問題箇所があったらエラーを表示する。問題が無かったらmodule:modules/miiafw/MiiaFwCognito#signupへユーザID、メールアドレス、パスワードを渡し、モーダルを閉じる
 */
const doSubmit = (): void => {
  // エラー表示初期化
  resetAlertView()
  // バリデーションチェック
  const errorList: string[] = []
  if (userName.value.length < 1) {
    alertView.un.hasError = true
    alertView.un.alertMessage = 'User IDは必須項目です'
  }
  if (mfaEnable.value) {
    if (phoneNumber.value.length < 1 && email.value.length < 1) {
      alertView.em.hasError = true
      alertView.pn.hasError = true
      errorList.push('携帯電話番号かメールアドレスのいずれかを設定する必要があります。')
    }
    if (phoneNumber.value.length < 1) {
      alertView.pn.hasError = true
      alertView.pn.alertMessage = '携帯電話番号は必須項目です'
    }
  } else {
    if (email.value.length < 1) {
      alertView.em.hasError = true
      alertView.em.alertMessage = 'メールアドレスは必須項目です'
    }
  }
  if (phoneNumber.value.length > 0) {
    const regexp = /^[0-9]{4,14}$/
    if (!regexp.test(phoneNumber.value)) {
      alertView.pn.hasError = true
      alertView.pn.alertMessage = '携帯電話番号の形式が不正です'
    }
  }
  if (email.value.length > 0) {
    const regexp =
      /^[a-zA-Z0-9_+-]+(\.[a-zA-Z0-9_+-]+)*@([a-zA-Z0-9][a-zA-Z0-9-]*[a-zA-Z0-9]*\.)+[a-zA-Z]{2,}$/
    if (!regexp.test(email.value)) {
      alertView.em.hasError = true
      alertView.em.alertMessage = 'メールアドレスの形式が不正です'
    }
  }
  if (password.value.length < 1) {
    alertView.pw.hasError = true
    alertView.pw.alertMessage = 'パスワードは必須項目です。'
  } else {
    const regexp = /^(?=.*[0-9])(?=.*[A-Z])(?=.*[a-z]).{8,}$/
    if (!regexp.test(password.value)) {
      alertView.pw.hasError = true
      alertView.pw.alertMessage =
        'パスワードは8文字以上で、大文字・小文字・数字は少なくとも1種類ずつ含めてください。'
    }
  }
  if (password2.value.length < 1) {
    alertView.pw2.hasError = true
    alertView.pw2.alertMessage = 'パスワードの再入力が必要です。'
  }
  if (
    password.value !== password2.value &&
    password.value.length > 0 &&
    password2.value.length > 0
  ) {
    alertView.pw.hasError = true
    alertView.pw2.hasError = true
    errorList.push('パスワードが不一致です。')
  }
  if (errorList.length > 0) {
    alertMessageLight.value?.addAlertMessage(errorList.join('\n'))
  } else if (
    !alertView.un.hasError &&
    !alertView.em.hasError &&
    !alertView.pn.hasError &&
    !alertView.pw.hasError &&
    !alertView.pw2.hasError
  ) {
    // 処理中表示
    setLoadingView(true)
    // サインアップ処理
    signUpApiCall(userName.value, email.value, phoneNumber.value, password.value)
  }
}

defineExpose({
  // data
  modalTop,
  myModal,
  userName,
  email,
  phoneNumber,
  password,
  password2,
  alertView,
  mfaEnable,

  // methods
  openModal,
  closeMyModal,
  closeModal,
  firstTab,
  lastTab,
  doReset,
  doSubmit
})
</script>

<template>
  <div v-if="modalTop">
    <!-- @closeを指定すると枠外クリック時にモーダルウィンドウを閉じる。 -->
    <!-- <MiiaFwAuthModal @close="closeModal" v-if="modal"> -->
    <MiiaFwAuthModal
      v-if="myModal"
      @push-ctl-delete="doReset"
      @push-esc="closeModal"
      @click-close="closeModal"
    >
      <!-- default スロットコンテンツ -->
      <h4 style="font-size: 1.1rem; font-weight: 500">{{ $t('Auth.signUp') }}</h4>
      <div style="padding: 30px 10px 30px 10px; margin-bottom: 10px; border: 1px solid #bcbcbc">
        <div
          class="form-group"
          style="text-align: left"
          v-bind:class="{ 'has-error': alertView.un.hasError }"
        >
          <label class="control-label">
            ユーザーID
            <span v-if="alertView.un.alertMessage !== ''" class="ff-alert-msg">
              <font-awesome-icon icon="exclamation-circle" />{{ alertView.un.alertMessage }}
            </span>
          </label>
          <input
            v-model="userName"
            ref="userNameInput"
            class="form-control ff-frm-ctrl"
            placeholder="管理者に指定されたIDを入力してください。"
            tabindex="1"
            @keydown.shift.tab="firstTab"
          />
        </div>
        <div v-if="mfaEnable">
          <div
            class="form-group"
            data-testid="signup-phone-number"
            style="text-align: left"
            v-bind:class="{ 'has-error': alertView.pn.hasError }"
          >
            <label class="control-label">
              電話番号
              <span v-if="alertView.pn.alertMessage !== ''" class="ff-alert-msg">
                <font-awesome-icon icon="exclamation-circle" />{{ alertView.pn.alertMessage }}
              </span>
            </label>
            <input
              v-model="phoneNumber"
              class="form-control ff-frm-ctrl"
              tabindex="2"
              placeholder="0x012345678"
            />
          </div>
        </div>
        <div v-if="mfaEnable">
          <div
            class="form-group"
            data-testid="signup-email"
            style="text-align: left"
            v-bind:class="{ 'has-error': alertView.em.hasError }"
          >
            <label class="control-label">
              【任意】メールアドレス
              <span v-if="alertView.em.alertMessage !== ''" class="ff-alert-msg">
                <font-awesome-icon icon="exclamation-circle" />{{ alertView.em.alertMessage }}
              </span>
            </label>
            <input
              v-model="email"
              class="form-control ff-frm-ctrl"
              tabindex="3"
              placeholder="xxx@sample.com"
            />
          </div>
          <p>※メールアドレスを登録して頂くと、自分でパスワードリセットできる様になります。</p>
        </div>
        <div v-else>
          <div
            class="form-group"
            style="text-align: left"
            v-bind:class="{ 'has-error': alertView.em.hasError }"
          >
            <label class="control-label">
              メールアドレス
              <span v-if="alertView.em.alertMessage !== ''" class="ff-alert-msg">
                <font-awesome-icon icon="exclamation-circle" />{{ alertView.em.alertMessage }}
              </span>
            </label>
            <input
              v-model="email"
              class="form-control ff-frm-ctrl"
              tabindex="3"
              placeholder="xxx@sample.com"
            />
          </div>
        </div>
        <div
          class="form-group"
          style="text-align: left"
          v-bind:class="{ 'has-error': alertView.pw.hasError }"
        >
          <label class="control-label">
            パスワード
            <span v-if="alertView.pw.alertMessage !== ''" class="ff-alert-msg">
              <font-awesome-icon icon="exclamation-circle" />{{ alertView.pw.alertMessage }}
            </span>
          </label>
          <input
            type="password"
            v-model="password"
            class="form-control ff-frm-ctrl"
            tabindex="4"
            placeholder="大文字、小文字、数字混合かつ8文字以上"
          />
        </div>
        <div
          class="form-group"
          style="text-align: left"
          v-bind:class="{ 'has-error': alertView.pw2.hasError }"
        >
          <label class="control-label">
            {{ $t('Auth.reenterPassword') }}
            <div
              v-if="alertView.pw2.alertMessage !== ''"
              class="ff-alert-msg"
              style="margin-left: 0px"
            >
              <font-awesome-icon icon="exclamation-circle" />{{ alertView.pw2.alertMessage }}
            </div>
          </label>
          <input
            type="password"
            v-model="password2"
            class="form-control ff-frm-ctrl"
            tabindex="5"
            placeholder="大文字、小文字、数字混合かつ8文字以上"
          />
        </div>
        <div style="text-align: right">
          <button
            class="btn ff-btn-primary"
            data-testid="do-subumit"
            tabindex="6"
            @click="doSubmit"
          >
            &nbsp;ユーザー登録
          </button>
        </div>
      </div>
      <MiiaFwAlertMessageLight ref="alertMessageLight"></MiiaFwAlertMessageLight>
      <!-- /default -->
    </MiiaFwAuthModal>
    <MiiaFwActivate ref="activate" @close-top="closeModal"></MiiaFwActivate>
  </div>
</template>
